import type { Farm } from '../models/Farm';
import type { State } from '../models/State';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import RHFInput from './RHF/RHFInput';
import { RHFSubmitButton } from './RHF/RHFSubmitButton';
import { RHFControlledInput } from './RHF/RHFControlledInput';
import { formatPhoneNumbers } from '../helpers/formatters';
import { RHFSelect } from './RHF/RHFSelect';
import FarmAddressApi from '../apis/farm_address_api';
import { handleError } from '../apis';
import { toast } from 'react-toastify';
import { farmKeys } from '../queries/FarmQueries';

const validator_farm = z.object({
  active: z.boolean(),
  farmId: z.number(),
  applicationUserId: z.string(),
  name: z.string('Required!').trim().min(1, 'Required'),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  stateId: z.number().nullish(),
  zipcode: z.string().trim().optional(),
  phoneNumber: z.string().trim().optional(),
  email: z.string().trim().optional(),
});

type FarmFormValues = z.infer<typeof validator_farm>;

export default function AddUpdateFarmForm({
  farm,
  states,
}: {
  farm: Farm;
  states: State[];
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: farm,
    resolver: zodResolver(validator_farm),
  });

  const activeValue = useWatch({ control, name: 'active' });

  const onHandleSubmit = async (values: FarmFormValues) => {
    let res: Farm;
    try {
      if (values.farmId) {
        res = await FarmAddressApi.updateFarm(values.farmId, values);
      } else {
        res = await FarmAddressApi.createFarm(values);
      }
      queryClient.invalidateQueries({ queryKey: farmKeys.all });
      return navigate(`/farms/${res.farmId}`);
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onHandleSubmit)}
      className='flex flex-col items-center justify-center'
    >
      <div className={`mb-2 flex py-2`}>
        <div
          className={`min-w-[275px] flex justify-center  rounded-lg bg-gray-100 px-4 py-3 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-300`}
        >
          <label className='flex items-center gap-2'>
            <input
              type='checkbox'
              checked={activeValue}
              {...register('active')}
              id='active'
              className={`rounded border-gray-300 bg-gray-100 text-teal-600 focus:ring-2 focus:ring-teal-500 dark:border-gray-600 ${
                activeValue ? 'bg-teal-600 dark:bg-teal-600' : ''
              } dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-teal-600`}
            />
            Active
          </label>
        </div>
      </div>
      <div className='alpaca-form-grid'>
        <RHFInput
          name='name'
          label='Name'
          type='text'
          className='min-w-[275px]'
          register={register('name')}
          errors={errors}
        />
        <RHFInput
          name='address1'
          label='Address 1'
          type='text'
          className='min-w-[275px]'
          register={register('address1')}
          errors={errors}
        />
        <RHFInput
          name='address2'
          label='Address 2'
          type='text'
          className='min-w-[275px]'
          register={register('address2')}
          errors={errors}
        />
        <RHFInput
          name='city'
          label='City'
          type='text'
          className='min-w-[275px]'
          register={register('city')}
          errors={errors}
        />
        <RHFSelect
          name='stateId'
          label='State'
          key={states.length}
          options={states}
          optionMethod={(states: State[]) =>
            states.map((g) => (
              <option value={g.stateId} key={g.stateId}>
                {g.abbreviation}
              </option>
            ))
          }
          className='min-w-[275px]'
          register={register('stateId', {
            setValueAs: (v) => (v === '' ? null : Number(v)),
          })}
          errors={errors}
        />
        <RHFInput
          name='zipcode'
          label='ZIP Code'
          type='text'
          className='min-w-[275px]'
          register={register('zipcode')}
          errors={errors}
        />
        <RHFControlledInput
          name='phoneNumber'
          label='Phone Number'
          type='text'
          className='min-w-[275px]'
          control={control}
          errors={errors}
          formatter={formatPhoneNumbers}
        />

        <RHFInput
          name='email'
          label='Email Address'
          type='text'
          className='min-w-[275px]'
          register={register('email')}
          errors={errors}
        />

        <button
          type='button'
          onClick={() => {
            reset();
            navigate(-1);
          }}
          className='my-2 max-w-[275px] rounded-md border border-slate-950 bg-transparent p-2 text-xs font-bold uppercase hover:border-gray-400 hover:text-gray-400 dark:border-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-300'
        >
          Cancel
        </button>
        <RHFSubmitButton label='Submit' isSubmitting={isSubmitting} />
      </div>
    </form>
  );
}
